import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TopDashboardSection from '../../../common/top-dashboard-section';
import Card from '../../../common/card';
import CardDescription from '../../../common/card-description';
import Dialog from '../../../common/dialog';
import PersonalInfo from '../personal-info';
import ChangePassword from '../change-password';
import TwoFactorAuth from '../two-factor-auth';
import l from '../../../../lang';
import { CircleUser, Lock, Pencil } from 'lucide-react';
import { segmentUserMenuProfileEdit, segmentUserMenuProfileChangePassword } from '../../../../utils/analytics';
import { useDashboard } from '../../../../store/dashboard';
import { AuthSelectors } from '../../../../store/auth/selectors';
import { openEditPersonalInfo, openChangePassword } from '../../../../store/profile/actions';
import './styles.scss';

const ProfileAccountPage = () => {
  const { userMe } = useDashboard();
  const roles = useSelector(AuthSelectors.getRoles);
  const dispatch = useDispatch();
  const [personalOpen, setPersonalOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  useEffect(() => {
    dispatch(openEditPersonalInfo(false));
    dispatch(openChangePassword(false));
    return () => {
      dispatch(openEditPersonalInfo(false));
      dispatch(openChangePassword(false));
    };
  }, [dispatch]);

  const closePersonal = () => {
    setPersonalOpen(false);
    dispatch(openEditPersonalInfo(false));
  };

  const closePassword = () => {
    setPasswordOpen(false);
    dispatch(openChangePassword(false));
  };

  const roleLabel =
    roles.length > 0 ? roles.map(role => l(`roles.${role}`)).join(', ') : '—';

  return (
    <div className="profile-account-page">
      <TopDashboardSection
        title={l('menu.item.SIDEBAR_PROFILE')}
        subtitle={l('profilePage.heroSubtitle')}
        showAvatarOnMobile
      />
      <Card>
        <CardDescription>{l('profilePage.accountSettings')}</CardDescription>
        <div className="profile-account-page__content">
          <div className="profile-account-page__panel">
              <section className="profile-account-page__section profile-account-page__section--personal">
                <div className="profile-account-page__strip">
                  <CircleUser className="profile-account-page__strip-icon" size={22} strokeWidth={1.35} aria-hidden />
                  <h3 className="profile-account-page__strip-title">{l('personalInfo.title')}</h3>
                </div>
                <div className="profile-account-page__section-body">
                  <div className="profile-account-page__grid profile-account-page__grid--personal">
                    <div className="profile-account-page__kv">
                      <div className="profile-account-page__label">{l('personalInfo.name')}</div>
                      <div className="profile-account-page__value">{userMe?.name}</div>
                    </div>
                    <div className="profile-account-page__kv">
                      <div className="profile-account-page__label">{l('personalInfo.email')}</div>
                      <div className="profile-account-page__value">{userMe?.email}</div>
                    </div>
                    <div className="profile-account-page__kv">
                      <div className="profile-account-page__label">{l('userManagement.role')}</div>
                      <div className="profile-account-page__value">{roleLabel}</div>
                    </div>
                    <div className="profile-account-page__kv profile-account-page__kv--idioma">
                      <div className="profile-account-page__label-row">
                        <span className="profile-account-page__label">{l('personalInfo.language')}</span>
                        <button
                          type="button"
                          className="profile-account-page__edit-link"
                          onClick={() => {
                            segmentUserMenuProfileEdit();
                            setPersonalOpen(true);
                          }}
                        >
                          <Pencil className="profile-account-page__edit-link-icon" size={16} strokeWidth={1.35} aria-hidden />
                          {l('personalInfo.edit')}
                        </button>
                      </div>
                      <div className="profile-account-page__value">
                        {userMe?.language ? l(`common.language.${userMe.language}`) : '—'}
                      </div>
                    </div>
                    <div className="profile-account-page__kv">
                      <div className="profile-account-page__label">{l('personalInfo.marketingConsent')}</div>
                      <div className="profile-account-page__value">
                        {l(`common.${userMe?.marketingConsent ? 'yes' : 'no'}`)}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="profile-account-page__section profile-account-page__section--security">
                <div className="profile-account-page__strip profile-account-page__strip--security">
                  <div className="profile-account-page__strip-head">
                    <Lock className="profile-account-page__strip-icon" size={22} strokeWidth={1.35} aria-hidden />
                    <h3 className="profile-account-page__strip-title">{l('changePassword.title')}</h3>
                  </div>
                  <div className="profile-account-page__strip-actions">
                    <button
                      type="button"
                      className="profile-account-page__outline-btn"
                      onClick={() => {
                        segmentUserMenuProfileChangePassword();
                        setPasswordOpen(true);
                      }}
                    >
                      <Pencil className="profile-account-page__outline-btn-icon" size={16} strokeWidth={1.35} aria-hidden />
                      {l('profilePage.changePasswordCta')}
                    </button>
                  </div>
                </div>
                <div className="profile-account-page__section-body profile-account-page__section-body--tight">
                  <div className="profile-account-page__grid profile-account-page__grid--single">
                    <div className="profile-account-page__kv">
                      <div className="profile-account-page__label">{l('profilePage.passwordField')}</div>
                      <div className="profile-account-page__value profile-account-page__value--mono">{l('profilePage.passwordMasked')}</div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="profile-account-page__section profile-account-page__section--twofa profile-account-page__section--last">
                <TwoFactorAuth layout="account" />
              </section>
          </div>
        </div>
      </Card>

      {personalOpen && (
        <Dialog
          type="sm"
          fullScreenMobile
          dividers={false}
          unpaddingContent
          title={l('personalInfo.title')}
          handleClose={closePersonal}
          body={<PersonalInfo variant="modal" onRequestClose={closePersonal} />}
        />
      )}

      {passwordOpen && (
        <Dialog
          type="sm"
          fullScreenMobile
          dividers={false}
          unpaddingContent
          title={l('changePassword.title')}
          handleClose={closePassword}
          body={<ChangePassword variant="modal" onRequestClose={closePassword} />}
        />
      )}
    </div>
  );
};

export default ProfileAccountPage;
